/**
 * identity-service calls of the register (pairing, PIN, device refresh, renew, manager approval)
 * on the generated client — the gateway routes `/api/v1/pos/registers/{id}/pair` and
 * `/api/v1/pos/auth/*` to identity-service (T01-05, T07-02).
 */
import { createIdentityClient, type IdentityClient } from "@tcg/api-client";

import type { ApprovalResult, IdentityApi, PairResult, PinResult } from "./auth";
import { apiOrigin } from "./env";
import { ApiError, unwrap } from "./errors";
import { newIdempotencyKey } from "./idempotency";

export interface IdentityOptions {
  baseUrl?: string;
  getLocale?: () => string | undefined;
  client?: IdentityClient;
  fetch?: typeof fetch;
}

async function guarded<T>(
  call: () => Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  let result: { data?: T; error?: unknown; response: Response };
  try {
    result = await call();
  } catch (cause) {
    throw ApiError.network(cause);
  }
  return unwrap(result);
}

const bearer = (token: string) => ({ Authorization: `Bearer ${token}` });

export function createIdentityApi(options: IdentityOptions = {}): IdentityApi {
  const client =
    options.client ??
    createIdentityClient({
      baseUrl: options.baseUrl ?? apiOrigin(),
      getLocale: options.getLocale,
      ...(options.fetch ? { fetch: options.fetch } : {}),
    });
  return {
    async pair(registerId, body): Promise<PairResult> {
      const headers = { "Idempotency-Key": newIdempotencyKey() };
      const data = await guarded(() =>
        registerId
          ? client.POST("/v1/pos/registers/{register_id}/pair", {
              params: { path: { register_id: registerId } },
              body,
              headers,
            })
          : // the typed code alone: identity knows which register it was made for
            client.POST("/v1/pos/auth/pair", { body, headers }),
      );
      return {
        deviceToken: data.deviceToken ?? null,
        deviceTokenExpiresAt: data.deviceTokenExpiresAt ?? null,
        accessToken: data.accessToken,
        expiresAt: data.expiresAt,
        device: { id: data.device.id, registerId: data.device.registerId, name: data.device.name },
      };
    },
    async refreshDevice(deviceToken): Promise<PairResult> {
      const data = await guarded(() =>
        client.POST("/v1/pos/auth/device/refresh", {
          body: { deviceToken },
          headers: { "Idempotency-Key": newIdempotencyKey() },
        }),
      );
      return {
        deviceToken: data.deviceToken ?? null,
        deviceTokenExpiresAt: data.deviceTokenExpiresAt ?? null,
        accessToken: data.accessToken,
        expiresAt: data.expiresAt,
        device: { id: data.device.id, registerId: data.device.registerId, name: data.device.name },
      };
    },
    async pinLogin(deviceAccessToken, body): Promise<PinResult> {
      const data = await guarded(() =>
        client.POST("/v1/pos/auth/pin", {
          body,
          headers: { ...bearer(deviceAccessToken), "Idempotency-Key": newIdempotencyKey() },
        }),
      );
      return data;
    },
    async renew(staffToken): Promise<PinResult> {
      const data = await guarded(() =>
        client.POST("/v1/pos/auth/renew", {
          headers: { ...bearer(staffToken), "Idempotency-Key": newIdempotencyKey() },
        }),
      );
      return data;
    },
    async approve(token, body): Promise<ApprovalResult> {
      const data = await guarded(() =>
        client.POST("/v1/pos/auth/approve", {
          body: { ...body, amount: body.amount ?? null },
          headers: { ...bearer(token), "Idempotency-Key": newIdempotencyKey() },
        }),
      );
      return {
        approvalToken: data.approvalToken,
        expiresAt: data.expiresAt,
        approverId: data.approverId,
        approverName: data.approverName,
      };
    },
  };
}
